// src/pages/SurveyPage.jsx
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import { get, post } from '../../helpers/apiHelper';
import { validateEmail } from '../../helpers/validate';
// Importa iconos de react-icons para las emociones
import {
    FaAngry,
    FaFrown,
    FaMeh,
    FaSmile,
    FaLaugh
} from 'react-icons/fa';

function SurveyPage() {
    const { surveyId } = useParams();
    const [survey, setSurvey] = useState(null);
    const [responses, setResponses] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        get(`surveys/${surveyId}`)
            .then((data) => {
                setSurvey(data);
                setLoading(false);
            })
            .catch((err) => {
                setError('Failed to load survey');
                setLoading(false);
            });
    }, [surveyId]);

    const handleChange = (questionIndex, option) => {
        setResponses(prev => ({
            ...prev,
            [questionIndex]: option
        }));
    };

    const handleSubmit = async () => {
        if (!survey.isActive) {
            setError('Esta encuesta no está activa en este momento.');
            return;
        }

        const unansweredQuestions = survey.questions.some(
            (q, index) => {
                // Para preguntas con opciones (NPS o emojis), la respuesta es obligatoria
                if (q.options && q.options.length > 0) {
                    return !responses[index];
                }
                // Para preguntas de texto libre, no son obligatorias por defecto
                // pero puedes cambiar esto si quieres que sean obligatorias
                return false;
            }
        );
        if (unansweredQuestions) {
            Swal.fire({
                icon: 'error',
                title: 'Encuesta Incompleta',
                text: 'Por favor, responde todas las preguntas obligatorias antes de enviar la encuesta.',
                confirmButtonColor: '#dc3545',
            });
            return;
        }

        // Confirmar envío de encuesta
        Swal.fire({
            title: '¿Estás listo para enviar la encuesta?',
            text: 'Puedes revisar las respuestas o enviar tus opiniones para ayudarnos a mejorar.',
            icon: 'question',
            showDenyButton: true,
            confirmButtonText: 'Enviar',
            denyButtonText: 'Revisar',
        }).then((result) => {
            if (result.isConfirmed) {
                collectContactInfo();
            } else if (result.isDenied) {
                Swal.fire('Revisa tus respuestas');
            }
        });
    };

    const collectContactInfo = () => {
        Swal.fire({
            title: 'Por favor ingresa tus datos de contacto',
            html: `
            <input type="text" id="name" class="swal2-input" placeholder="Su nombre (opcional)">
            <input type="email" id="email" class="swal2-input" placeholder="Su Email *">
            <input type="text" id="phone" class="swal2-input" placeholder="Su teléfono (opcional)">`,
            focusConfirm: false,
            preConfirm: () => {
                const name = Swal.getPopup().querySelector('#name').value.trim();
                const email = Swal.getPopup().querySelector('#email').value.trim();
                const phone = Swal.getPopup().querySelector('#phone').value.trim();

                if (!email || !validateEmail(email)) {
                    Swal.showValidationMessage('Por favor, ingresa un email válido');
                }
                return { name, email, phone };
            }
        }).then((result) => {
            if (result.value) {
                submitSurvey(result.value);
            }
        });
    };

    const submitSurvey = (contactInfo) => {
        setSubmitting(true);

        if (!contactInfo.email || !validateEmail(contactInfo.email)) {
            delete contactInfo.email;
        }

        const payload = {
            surveyId,
            answers: responses,
            ...contactInfo
        };

        post(`survey-responses/${surveyId}`, payload)
            .then(() => {
                Swal.fire({
                    title: '¡Encuesta enviada!',
                    text: 'Muchas gracias por tus comentarios.',
                    icon: 'success',
                    confirmButtonColor: '#3085d6'
                });
                setResponses({});
                setSubmitting(false);
            })
            .catch((err) => {
                console.error('Failed to submit survey', err);
                setError('Failed to submit survey');
                setSubmitting(false);
                Swal.fire({
                    title: 'Ha habido un error al enviar la encuesta',
                    text: 'Hubo un problema al enviar su encuesta.',
                    icon: 'error',
                    confirmButtonColor: '#dc3545'
                });
            });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Cargando encuesta...</h2>
                    <p className="text-gray-600">Preparando las preguntas para ti</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="text-red-500 text-4xl mb-4">⚠️</div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    if (!survey) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="text-gray-400 text-4xl mb-4">📋</div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Encuesta no encontrada</h2>
                    <p className="text-gray-600">La encuesta que buscas no existe o ha sido eliminada</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <div className="container mx-auto px-4 py-8 max-w-4xl">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
                    <div className="text-center">
                        <div className="text-4xl mb-4">📊</div>
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                            {survey.title}
                        </h1>
                        <p className="text-gray-600 text-sm md:text-base">
                            Tu opinión es muy importante para nosotros. Por favor, tómate un momento para responder estas preguntas.
                        </p>
                    </div>
                </div>

                {/* Questions */}
                <div className="space-y-6">
                    {survey.questions.map((q, questionIndex) => {
                        const isNpsQuestion = q.type === "likert" && q.question === "¿Qué tan probable es que recomiendes nuestro comercio a un amigo?";
                        const isAnswered = q.options && q.options.length > 0
                            ? responses[questionIndex]
                            : responses[questionIndex] && responses[questionIndex].trim().length > 0;

                        return (
                            <div key={questionIndex} className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                                {/* Question Header */}
                                <div className="text-center mb-6">
                                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                                        <span className="text-blue-600 font-semibold text-lg">{questionIndex + 1}</span>
                                    </div>
                                    <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-gray-900 leading-tight">
                                        {q.question}
                                    </h2>
                                </div>

                                {/* Options */}
                                <div className="space-y-4">
                                    {q.options && q.options.length > 0 ? (
                                        isNpsQuestion ? (
                                            // NPS Question - Horizontal buttons
                                            <div className="flex flex-wrap justify-center gap-3">
                                                {q.options.map((option, optionIndex) => (
                                                    <button
                                                        key={optionIndex}
                                                        onClick={() => handleChange(questionIndex, option)}
                                                        className={`survey-nps-button ${responses[questionIndex] === option
                                                            ? 'survey-nps-button-active'
                                                            : 'survey-nps-button-inactive'}`}
                                                    >
                                                        <span className="text-lg font-semibold">{option}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            // Emoji Question - Grid layout with icons
                                            <div className="grid grid-cols-5 gap-4 md:gap-6">
                                                {q.options.map((option, optionIndex) => {
                                                    const emojiIcons = [FaAngry, FaFrown, FaMeh, FaSmile, FaLaugh];
                                                    const emojiLabels = ['Muy Enfadado', 'Enfadado', 'Neutral', 'Feliz', 'Muy Feliz'];
                                                    const emojiColors = ['text-red-500', 'text-orange-500', 'text-yellow-500', 'text-green-500', 'text-blue-500'];
                                                    const IconComponent = emojiIcons[optionIndex];

                                                    return (
                                                        <button
                                                            key={optionIndex}
                                                            onClick={() => handleChange(questionIndex, option)}
                                                            className={`survey-emoji-button ${responses[questionIndex] === option
                                                                ? 'survey-emoji-button-active'
                                                                : 'survey-emoji-button-inactive'}`}
                                                        >
                                                            <div className="survey-emoji-container">
                                                                <IconComponent
                                                                    className={`survey-emoji-icon ${emojiColors[optionIndex]}`}
                                                                />
                                                            </div>
                                                            <span className="survey-emoji-label">{option}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )
                                    ) : (
                                        // Text Question (Open-ended)
                                        <div className="max-w-2xl mx-auto">
                                            <div className="bg-gray-50 rounded-xl p-4 mb-4">
                                                <div className="flex items-center mb-3">
                                                    <span className="text-2xl mr-3">✍️</span>
                                                    <span className="text-sm font-medium text-gray-600">Respuesta de texto libre</span>
                                                </div>
                                                <textarea
                                                    className="survey-textarea"
                                                    value={responses[questionIndex] || ''}
                                                    onChange={(e) => handleChange(questionIndex, e.target.value)}
                                                    placeholder="Escribe tu respuesta aquí... Comparte tus pensamientos, sugerencias o comentarios."
                                                    rows={6}
                                                />
                                                <div className="mt-2 text-xs text-gray-500">
                                                    Puedes escribir tanto como quieras. Tu opinión es muy valiosa para nosotros.
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Progress indicator */}
                                <div className="mt-6 pt-4 border-t border-gray-100">
                                    <div className="flex items-center justify-between text-sm text-gray-500">
                                        <span>Pregunta {questionIndex + 1} de {survey.questions.length}</span>
                                        {isAnswered && (
                                            <span className="text-green-600 font-medium flex items-center">
                                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                                Respondida
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Submit Button */}
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mt-8">
                    <div className="flex flex-col items-center justify-center">
                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="survey-submit-button"
                        >
                            {submitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <span className="text-xl mr-2">📤</span>
                                    Enviar Encuesta
                                </>
                            )}
                        </button>

                        <p className="text-gray-500 text-sm mt-4 text-center max-w-md">
                            Al enviar la encuesta, aceptas que tus respuestas sean utilizadas para mejorar nuestros servicios.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SurveyPage;
